/**
 * Click-toggle spotlight zoom (forked from Dawn's magnify.js).
 *
 * Click the product image to activate:
 *   - a translucent LENS rectangle that follows the cursor on the image
 *   - a side PANEL pinned to the right of the image showing the magnified
 *     portion under the lens
 *
 * Click the image again, click outside, or press Escape to dismiss.
 *
 * Activation: the section's `image_zoom` setting must be "hover" so
 * `product-thumbnail.liquid` adds `.image-magnify-hover` to the <img>
 * and `main-product.liquid` loads this script. (The class name is kept
 * for compatibility with Dawn's CSS hook even though we're click-based.)
 *
 * Desktop only. On mobile / coarse pointers Dawn's separate
 * `.product__media-toggle` button (visible at ≤749px) opens the lightbox.
 */
(function () {
  if (typeof window === 'undefined') return;

  // Amazon-style sizing: panel matches image height, lens is rectangular
  // and proportional to the panel so zoom is uniform (no stretching).
  // Lens dimensions are derived in activate() once the image rect is known.
  // Panel sized as a percentage of the image in BOTH dimensions at
  // activation time, then capped to viewport room (width) and viewport
  // height. Lens dimensions are derived so panel/lens ratio === ZOOM_RATIO
  // on both axes (uniform zoom, no stretching).
  //
  // Important constraint: lens must be ≤ image. With panel_w = image_w *
  // SCALE and lens_w = panel_w / ZOOM, lens_w ≤ image_w requires
  // SCALE ≤ ZOOM. We pick SCALE = ZOOM * 0.7 so the lens covers ~70% of
  // image dims — visually substantial without filling the whole image.
  const ZOOM_RATIO = 2.5;
  const PANEL_SCALE = ZOOM_RATIO * 0.7; // → 1.75 (lens covers 70% of image)
  const PANEL_WIDTH_MIN = 480;
  const PANEL_MAX_DIM = 2400;
  const PANEL_GAP = 16;
  let active = null; // { image, lens, panel, onMove, onScroll, onResize, onKey, onDocClick }

  function isDesktop() {
    return window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
  }

  function highResSrc(image) {
    const src = image.currentSrc || image.src || '';
    return src.replace(/_(\d+)x(\d*)\./, '_2000x.');
  }

  // Compute the actual displayed image rect inside the <img> element.
  // Theme uses `object-fit: contain` on .media > img, so when the natural
  // aspect != wrapper aspect, the rendered pixels are letterboxed inside
  // the element. Lens & panel math must use the rendered rect, not the
  // wrapper rect, otherwise hovering the letterbox shifts the panel view
  // away from where the lens is sitting.
  function getDisplayedRect(image) {
    const r = image.getBoundingClientRect();
    const natW = image.naturalWidth;
    const natH = image.naturalHeight;
    const make = (left, top, width, height) => ({
      left, top, width, height,
      right: left + width,
      bottom: top + height,
    });
    if (!natW || !natH || r.width === 0 || r.height === 0) {
      // Natural dims unknown — assume the rendered rect fills the element.
      return make(r.left, r.top, r.width, r.height);
    }
    const wrapAR = r.width / r.height;
    const imgAR = natW / natH;
    if (Math.abs(imgAR - wrapAR) < 0.001) {
      // Same aspect, no letterbox.
      return make(r.left, r.top, r.width, r.height);
    }
    if (imgAR > wrapAR) {
      // Image is wider than wrapper → letterbox top/bottom
      const dispH = r.width / imgAR;
      return make(r.left, r.top + (r.height - dispH) / 2, r.width, dispH);
    }
    // Image is taller than wrapper → letterbox left/right
    const dispW = r.height * imgAR;
    return make(r.left + (r.width - dispW) / 2, r.top, dispW, r.height);
  }

  // Returns the viewport-y at which the sticky header ends. Lens and
  // panel must not render above this — otherwise they overlay the
  // (sticky) site nav, which looks broken when the user has scrolled
  // the product image partly behind it.
  function getHeaderFloor() {
    const sticky = document.querySelector(
      'sticky-header, .shopify-section-header, .section-header'
    );
    if (!sticky) return 0;
    const cs = getComputedStyle(sticky);
    if (cs.position !== 'sticky' && cs.position !== 'fixed') return 0;
    const r = sticky.getBoundingClientRect();
    return Math.max(0, r.bottom);
  }

  function buildLens() {
    const lens = document.createElement('div');
    lens.className = 'spotlight-zoom-lens is-visible';
    lens.setAttribute('aria-hidden', 'true');
    // Width/height set in activate() — depends on image rect
    document.body.appendChild(lens);
    return lens;
  }

  function buildPanel(image) {
    const panel = document.createElement('div');
    panel.className = 'spotlight-zoom-panel is-visible';
    panel.setAttribute('aria-hidden', 'true');
    panel.style.backgroundImage = "url('" + highResSrc(image) + "')";
    document.body.appendChild(panel);
    return panel;
  }

  // Computes final panel dimensions using the image's NATURAL aspect
  // (not the displayed rect aspect). Critical: Shopify often serves
  // images stretched to a different aspect via CSS (e.g., natural 506×506
  // displayed as 614×500). If we sized the panel to the displayed rect,
  // the panel would reproduce the same warping at much larger scale —
  // very obvious distortion in the magnified view.
  //
  // Falls back to the displayed rect if naturalWidth/Height aren't yet
  // available (image still loading).
  function pickPanelSize(image) {
    const r = getDisplayedRect(image);
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    const natW = image.naturalWidth || r.width;
    const natH = image.naturalHeight || r.height;

    let panelW = natW * PANEL_SCALE;
    let panelH = natH * PANEL_SCALE;

    // Horizontal cap: panel must fit next to the image (left or right),
    // and never exceed PANEL_MAX_DIM.
    const roomRight = vw - r.right - PANEL_GAP - 8;
    const roomLeft = r.left - PANEL_GAP - 8;
    const roomX = Math.max(roomRight, roomLeft);
    const wCapped = Math.min(panelW, roomX, PANEL_MAX_DIM);
    const wCappedFloor = Math.max(PANEL_WIDTH_MIN, wCapped);

    // Vertical cap: room from the image's top down to viewport bottom
    // (not the full viewport height) so the panel stays anchored at
    // the image top and just gets shorter when room runs out. Avoids
    // the panel "sliding up" past the image and looking detached.
    const roomBelow = Math.max(120, vh - Math.max(r.top, 0) - 32);
    const hCapped = Math.min(panelH, roomBelow, PANEL_MAX_DIM);

    // If one axis got capped tighter than the other, shrink the OTHER
    // axis by the same factor so the natural aspect ratio is preserved.
    const wScale = wCappedFloor / panelW;
    const hScale = hCapped / panelH;
    const finalScale = Math.min(wScale, hScale);

    return {
      w: panelW * finalScale,
      h: panelH * finalScale,
    };
  }

  function positionPanel(image, panel) {
    const r = getDisplayedRect(image);
    const panelW = parseFloat(panel.style.width) || PANEL_WIDTH_MIN;
    const vw = document.documentElement.clientWidth;

    // Horizontal: prefer right side of image; fall back to left if no
    // room; final clamp so panel never extends past viewport right edge.
    const right = r.left + r.width;
    const roomRight = vw - right - PANEL_GAP;
    let left =
      roomRight >= panelW
        ? right + PANEL_GAP
        : Math.max(8, r.left - panelW - PANEL_GAP);
    left = Math.min(left, vw - panelW - 8);

    // Vertical: anchor to image top — panel scrolls naturally with the
    // page. update() applies a clip-path to hide the portion that goes
    // behind the sticky header.
    panel.style.left = left + window.scrollX + 'px';
    panel.style.top = (r.top + window.scrollY) + 'px';
  }

  function update(image, lens, panel, clientX, clientY) {
    const r = getDisplayedRect(image);
    const lensW = lens.offsetWidth;
    const lensH = lens.offsetHeight;
    if (!lensW || !lensH) return;

    // Hide entirely only when the image is so obscured there's nothing
    // useful to show (less than ~40px visible below the header).
    const headerFloor = getHeaderFloor();
    const rBottom = r.top + r.height;
    const visibleH = rBottom - Math.max(r.top, headerFloor);
    if (visibleH < 40) {
      lens.style.visibility = 'hidden';
      panel.style.visibility = 'hidden';
      return;
    }
    lens.style.visibility = 'visible';
    panel.style.visibility = 'visible';

    const x = clientX - r.left;
    const y = clientY - r.top;

    // Standard image-bounds clamp (lens stays within IMG element).
    const lensX = Math.max(0, Math.min(x - lensW / 2, r.width - lensW));
    const lensY = Math.max(0, Math.min(y - lensH / 2, r.height - lensH));

    lens.style.left = r.left + window.scrollX + lensX + 'px';
    lens.style.top = r.top + window.scrollY + lensY + 'px';

    // Clip lens + panel independently when they extend above the sticky
    // header. Both are anchored to the image and scroll with the page
    // (non-sticky). Clip-path hides the portion behind the header so
    // they don't visually overlay the nav.
    const panelW = panel.offsetWidth;
    const panelH = panel.offsetHeight;
    if (!panelW || !panelH) return;

    // No clip-path needed — lens and panel have z-index 999, sticky
    // header z-index 1000. Header naturally covers them when scrolled
    // behind it. This preserves the full box-shadow on all sides of
    // the panel (clip-path would have removed shadow extending past
    // the element's bounds).

    // Background sized to (panel / lensFrac) so the lens region of the
    // image exactly fills the panel. Aspect of bgSize === aspect of panel
    // === natural aspect, so the source image renders without stretching
    // even if the displayed thumbnail is itself stretched.
    const bgW = panelW * (r.width / lensW);
    const bgH = panelH * (r.height / lensH);
    const ratioX = r.width - lensW > 0 ? lensX / (r.width - lensW) : 0;
    const ratioY = r.height - lensH > 0 ? lensY / (r.height - lensH) : 0;
    panel.style.backgroundSize = bgW + 'px ' + bgH + 'px';
    panel.style.backgroundPosition =
      '-' + ratioX * (bgW - panelW) + 'px -' + ratioY * (bgH - panelH) + 'px';
  }

  function deactivate() {
    if (!active) return;
    const { image, lens, panel, onMove, onScroll, onResize, onKey, onDocClick } = active;
    image.classList.remove('spotlight-active');
    document.removeEventListener('mousemove', onMove);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onDocClick, true);
    if (lens && lens.parentNode) lens.parentNode.removeChild(lens);
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
    active = null;
  }

  function activate(image, clickEvent) {
    deactivate();
    const lens = buildLens();
    const panel = buildPanel(image);
    image.classList.add('spotlight-active');

    // Track the last cursor position in viewport coords so we can
    // re-run update() on scroll/resize without needing a mousemove.
    let lastX = clickEvent.clientX;
    let lastY = clickEvent.clientY;

    // Panel sized to natural aspect (not displayed/warped aspect),
    // then positioned. After cap, the actual panel-to-natural scale
    // may be less than PANEL_SCALE.
    const size = pickPanelSize(image);
    panel.style.width = size.w + 'px';
    panel.style.height = size.h + 'px';
    positionPanel(image, panel);

    // Lens lives on the displayed image — its visual dims are in
    // displayed-screen pixels (the post-object-fit-contain rect, NOT
    // the wrapper rect). The natural region the lens represents =
    // (panelSize / ZOOM_RATIO) in natural coords. That natural region
    // maps to displayed coords by scaling (dispDim / natDim). Result:
    // lens displayed dim = dispDim × frac, where frac =
    // (actualPanelScale / ZOOM_RATIO).
    const r = getDisplayedRect(image);
    const natW = image.naturalWidth || r.width;
    const natH = image.naturalHeight || r.height;
    const actualScale = size.w / natW;
    const lensFrac = actualScale / ZOOM_RATIO;
    lens.style.width = r.width * lensFrac + 'px';
    lens.style.height = r.height * lensFrac + 'px';

    update(image, lens, panel, clickEvent.clientX, clickEvent.clientY);

    const onMove = (e) => {
      const r = getDisplayedRect(image);
      if (
        e.clientX < r.left ||
        e.clientX > r.left + r.width ||
        e.clientY < r.top ||
        e.clientY > r.top + r.height
      ) {
        return; // outside displayed image (or in letterbox) — leave lens at last position
      }
      lastX = e.clientX;
      lastY = e.clientY;
      update(image, lens, panel, lastX, lastY);
    };
    // On scroll/resize, the image rect shifts under the cursor — re-run
    // update() with the last cursor position so the lens & panel re-clip
    // and re-clamp against the (possibly newly-overlapped) header.
    const onScroll = () => {
      positionPanel(image, panel);
      update(image, lens, panel, lastX, lastY);
    };
    const onResize = () => {
      positionPanel(image, panel);
      update(image, lens, panel, lastX, lastY);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') deactivate();
    };
    const onDocClick = (e) => {
      if (image.contains(e.target) || panel.contains(e.target) || lens.contains(e.target)) {
        return;
      }
      deactivate();
    };

    document.addEventListener('mousemove', onMove);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    // capture-phase so we beat the document body's other click handlers
    setTimeout(() => document.addEventListener('click', onDocClick, true), 0);

    active = { image, lens, panel, onMove, onScroll, onResize, onKey, onDocClick };
  }

  function bindImage(image) {
    if (image.dataset.spotlightBound === '1') return;
    image.dataset.spotlightBound = '1';
    image.style.cursor = 'pointer';
    image.addEventListener('mouseenter', (e) => {
      if (!isDesktop()) return; // mobile uses Dawn's lightbox button instead
      activate(image, e);
    });
    image.addEventListener('mouseleave', () => {
      if (active && active.image === image) deactivate();
    });
  }

  // Called by product-info.js after variant changes re-render the gallery.
  // Name kept for compatibility with existing `enableZoomOnHover(2)` call.
  function enableZoomOnHover() {
    document.querySelectorAll('.image-magnify-hover').forEach(bindImage);
  }
  window.enableZoomOnHover = enableZoomOnHover;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableZoomOnHover);
  } else {
    enableZoomOnHover();
  }
})();
